import { useMemo, useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES } from "@/utils/countries";

const OTHER_SENTINEL = "__other__";

/**
 * Country dropdown that prioritizes countries already present in the trip and
 * offers an "Other country…" option to reveal the full list.
 *
 * Props:
 *   value              - current country name
 *   onChange(value)    - setter
 *   tripCountries      - string[] of countries already in this trip (sorted)
 *   id, placeholder, testId — passthrough
 */
const CountrySelectWithOther = ({
  value,
  onChange,
  tripCountries = [],
  id = "country",
  placeholder = "Select country",
  testId = "segment-country-select",
}) => {
  // If the current value is not in trip countries, the user has already picked
  // an "Other" country — keep the full list visible so they can change it.
  const valueInTrip = !value || tripCountries.includes(value);
  const [showAll, setShowAll] = useState(!valueInTrip);

  useEffect(() => {
    if (!valueInTrip && !showAll) setShowAll(true);
  }, [valueInTrip, showAll]);

  const otherCountries = useMemo(() => {
    const set = new Set(tripCountries);
    return COUNTRIES.filter(c => !set.has(c));
  }, [tripCountries]);

  const handleChange = (v) => {
    if (v === OTHER_SENTINEL) {
      setShowAll(true);
      return; // don't persist the sentinel — user picks again from expanded list
    }
    onChange(v);
  };

  return (
    <Select value={value} onValueChange={handleChange}>
      <SelectTrigger id={id} data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent position="popper" className="max-h-[300px]">
        {tripCountries.length > 0 && (
          <>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              In this trip
            </div>
            {tripCountries.map((country) => (
              <SelectItem key={`trip-${country}`} value={country}>
                {country}
              </SelectItem>
            ))}
            {!showAll && (
              <SelectItem value={OTHER_SENTINEL} data-testid="country-select-other">
                — Other country… —
              </SelectItem>
            )}
          </>
        )}
        {(showAll || tripCountries.length === 0) && (
          <>
            {tripCountries.length > 0 && (
              <div className="px-2 py-1 mt-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-t dark:border-gray-700">
                All countries
              </div>
            )}
            {otherCountries.map((country) => (
              <SelectItem key={country} value={country}>
                {country}
              </SelectItem>
            ))}
          </>
        )}
      </SelectContent>
    </Select>
  );
};

export default CountrySelectWithOther;
